// ---------------------------------------------------------------------------
// planogramViewQuery
//
// GH #569: the Tabular/Visual tab selection must survive every preview <->
// edit transition — opening Edit from a given preview tab must land on the
// matching editor tab, and Save/Cancel from the editor must return to the
// matching preview tab. Both PlanogramPreviewPage's TabView and
// PlanogramDetails' TabView currently track activeTabIndex as plain local
// React state with no relationship to each other at all, and the only
// editor route that exists (/planograms/edit/:planogramId/visual) has no
// per-tab segment — so there is nowhere for "which tab" to live except a
// URL query param that both pages read on mount and keep in sync as the
// user switches tabs.
//
// This file is the single source of truth for that param's name and the
// index<->name mapping, imported by both PlanogramPreviewPage.tsx and
// PlanogramDetails.tsx so the two pages can never disagree about what
// `tabular`/`visual`/0/1 mean.
// ---------------------------------------------------------------------------

export type PlanogramViewTab = 'tabular' | 'visual';

export const PLANOGRAM_VIEW_QUERY_PARAM = 'planogramView';

/** Tab index 0 is always Tabular, 1 is always Visual, in both TabViews (PlanogramPreviewPage and PlanogramDetails already order their TabPanels this way — Tabular first, Visual second — so this mapping just names that existing order rather than changing it). */
export const normalizePlanogramView = (value: string | null | undefined): PlanogramViewTab =>
    value === 'visual' ? 'visual' : 'tabular';

export const getPlanogramViewByTabIndex = (index: number): PlanogramViewTab =>
    index === 1 ? 'visual' : 'tabular';

export const getTabIndexByPlanogramView = (value: string | null | undefined): number =>
    normalizePlanogramView(value) === 'visual' ? 1 : 0;

/**
 * Appends/overwrites the planogramView query param on a path that may or
 * may not already have a query string. Used for the two cross-page
 * navigations (preview -> edit, edit -> preview) where the destination is
 * a fresh path string rather than something already going through
 * useSearchParams on the CURRENT page.
 */
export const withPlanogramViewQuery = (path: string, view: PlanogramViewTab): string => {
    const [base, existingQuery] = path.split('?');
    const params = new URLSearchParams(existingQuery);
    params.set(PLANOGRAM_VIEW_QUERY_PARAM, view);
    return `${base}?${params.toString()}`;
};